import { useMutation, useQuery } from '@apollo/client'
import { ALL_FIXED_ADDONS } from './queries'
import { useConfirm, useNodes, usePermissions } from '@/utils/hooks'
import { OldAddButton, Header } from '@/modules/companies'
import { Table } from '@/components_deprecated/Table'
import { format, parseISO } from 'date-fns'
import styled from 'styled-components'
import {
  Icon,
  Loader,
  usePrompt,
  useToast,
} from '@ur-solutions/react-components'
import { Card } from '@/components_deprecated/Card'
import { useTranslate } from '@ur-solutions/react-hooks'
import type { FixedAddonForm } from './types'
import type { FixedAddonNode } from './types.graphql'
import { PERMISSIONS } from '@/utils/permissions'
import { EditFixedAddonPrompt } from './EditFixedAddonPrompt'
import {
  CREATE_FIXED_ADDON,
  DELETE_FIXED_ADDON,
  PATCH_FIXED_ADDON,
} from './mutations'

const Wrapper = styled(Card)`
  position: static;
  margin: 1rem 0;
`

const H2 = styled.h2`
  display: flex;

  margin: 1rem 0 0 8px;
  font-weight: 600;

  div {
    margin-left: 1rem;
  }
`

interface FixedAddonsProps {
  datetimeStart: Date
  datetimeEnd: Date
  userId: string
  onChange?: () => void
}

export const FixedAddons: React.JSX.CFC<FixedAddonsProps> = ({
  datetimeStart,
  datetimeEnd,
  userId,
  onChange,
}) => {
  const translations = useTranslate({
    fixedAddons: 'salary.fixed-addons',
    amount: 'common.amount',
    overrideOtherAddons: 'salary.override-other-addons',

    date: 'common.date',
    salaryCode: 'common.salary-code',
    edit: 'common.edit',
    delete: 'common.delete',

    deleteFixedAddon: 'salary.delete-fixed-addon',
    deleteFixedAddonConfirm: 'salary.delete-fixed-addon-confirm',
    comment: 'common.comment',

    toast: {
      successCreate: 'salary.fixed-addon-create-success',
      successUpdate: 'salary.fixed-addon-update-success',
      successDelete: 'salary.fixed-addon-delete-success',
    },
  })

  const addPrompt = usePrompt()
  const addToast = useToast()
  const confirm = useConfirm()
  const { hasPermissions } = usePermissions()

  const canAdd = hasPermissions(PERMISSIONS.salary.add.fixedaddon, true)

  const canEdit = hasPermissions(PERMISSIONS.salary.change.fixedaddon, true)
  const canDelete = hasPermissions(PERMISSIONS.salary.delete.fixedaddon, true)

  const { data, loading } = useQuery(ALL_FIXED_ADDONS, {
    variables: {
      dateStart: format(datetimeStart, 'yyyy-MM-dd'),
      dateEnd: format(datetimeEnd, 'yyyy-MM-dd'),
      userId: userId,
    },
  })

  const refetchQueries = [
    {
      query: ALL_FIXED_ADDONS,
      variables: {
        dateStart: format(datetimeStart, 'yyyy-MM-dd'),
        dateEnd: format(datetimeEnd, 'yyyy-MM-dd'),
        userId: userId,
      },
    },
    'DriverActivityPeriodReport',
    'DriverActivityPeriodReportShallow',
    'AllDynamicColumnsQuery',
    'DriverActivityDayReport',
  ]

  const [createFixedAddon] = useMutation(CREATE_FIXED_ADDON, {
    refetchQueries: refetchQueries,
    onCompleted: () => {
      addToast('success', translations.toast.successCreate)
      onChange?.()
    },
  })

  const [patchFixedAddon] = useMutation(PATCH_FIXED_ADDON, {
    refetchQueries: refetchQueries,
    onCompleted: () => {
      addToast('success', translations.toast.successUpdate)
      onChange?.()
    },
  })

  const [deleteFixedAddon] = useMutation(DELETE_FIXED_ADDON, {
    refetchQueries: refetchQueries,
    onCompleted: () => {
      addToast('success', translations.toast.successDelete)
      onChange?.()
    },
  })

  const timeAddons = useNodes(data?.allFixedAddons)

  const handleAdd = async () => {
    const { data } = await addPrompt<FixedAddonForm | null>(resolve => (
      <EditFixedAddonPrompt
        initialData={{
          date: datetimeStart,
          salaryCode: null,
          amount: 0,
          comment: '',
        }}
        userId={userId}
        onSubmit={resolve}
      />
    ))

    if (!data || !data.salaryCode) return

    void createFixedAddon({
      variables: {
        input: {
          user: userId,
          date: format(data.date, 'yyyy-MM-dd'),
          salaryCode: data.salaryCode,
          amount: data.amount,
          comment: data.comment,
        },
      },
    })
  }

  const handleEdit = async (addon: FixedAddonNode) => {
    const initialData = {
      date: addon.date ? parseISO(addon.date) : new Date(),
      salaryCode: addon.salaryCode?.id ?? '',
      amount: addon.amount,
      comment: addon.comment,
    }
    const { data } = await addPrompt<FixedAddonForm | null>(resolve => (
      <EditFixedAddonPrompt
        initialData={initialData}
        userId={userId}
        onSubmit={resolve}
      />
    ))

    if (!data || !data.salaryCode) return

    void patchFixedAddon({
      variables: {
        id: addon.id,
        input: {
          date: format(data.date, 'yyyy-MM-dd'),
          salaryCode: data.salaryCode,
          amount: data.amount,
          comment: data.comment,
        },
      },
    })
  }

  const handleDelete = async (addon: FixedAddonNode) => {
    const { data } = await confirm(
      translations.deleteFixedAddonConfirm,
      translations.deleteFixedAddon
    )

    if (!data) return

    void deleteFixedAddon({
      variables: {
        id: addon.id,
      },
    })
  }

  const numUtilColumns = (canEdit ? 1 : 0) + (canDelete ? 1 : 0)

  return (
    <Wrapper>
      <Header>
        <H2>{translations.fixedAddons}</H2>
        {loading && <Loader.Spinner />}
        {!loading && canAdd && (
          <OldAddButton adding={false} onClick={handleAdd} />
        )}
      </Header>
      {!loading && (
        <Table
          noShadow
          noBorders
          noHeaderMargin
          noRowMargin
          rowHoverColor="secondary"
        >
          <thead>
            <tr>
              <th>{translations.date}</th>
              <th>{translations.amount}</th>
              <th>{translations.salaryCode}</th>
              <th>{translations.comment}</th>
              {(canEdit || canDelete) && <th colSpan={numUtilColumns}></th>}
            </tr>
          </thead>
          <tbody>
            {timeAddons.map(fixedAddon => (
              <tr key={fixedAddon.id}>
                <td>
                  {fixedAddon.date
                    ? format(parseISO(fixedAddon.date), 'dd. MMM')
                    : '-'}
                </td>
                <td>{fixedAddon.amount}</td>
                <td>
                  {fixedAddon.salaryCode ? fixedAddon.salaryCode.name : '-'}
                </td>
                <td>{fixedAddon.comment}</td>
                {canEdit && (
                  <td width="1px">
                    <Icon
                      icon="edit"
                      cursor="pointer"
                      color="gray6"
                      hoverColor="secondary"
                      onClick={() => handleEdit(fixedAddon)}
                    />
                  </td>
                )}
                {canDelete && (
                  <td width="1px">
                    <Icon
                      icon="times"
                      cursor="pointer"
                      color="gray6"
                      hoverColor="red"
                      onClick={() => handleDelete(fixedAddon)}
                    />
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Wrapper>
  )
}
